import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';

import {BehaviorSubject, combineLatest, map, Observable, shareReplay, Subscription} from 'rxjs';

import {FilterService, Message} from 'primeng/api';
import {OverlayPanel} from "primeng/overlaypanel";
import {Calendar} from "primeng/calendar";
import {ColumnFilterFormElement, Table} from "primeng/table";
import { DialogService } from 'primeng/dynamicdialog';

import {DateTime} from 'luxon';

import { AuthService } from '@shared/authentication/auth.service';
import {FileType} from "@shared/components/file-import/file-import.component";
import {BlobStorageComponent} from "@shared/components/blob-storage/blob-storage.component";
import {getSiteInfo} from "@shared/helpers/site-info";
import {ActivityBlockingService} from '@shared/services/activity-blocking.service';
import { MessageService } from '@shared/services/message-service';

import {SiteSubinventory, SubinventoryType} from '@shared/models/subinventory';
import {ShiftStatus} from '@shared/models/worked-shift';
import {HistoryResponse} from "@shared/models/history";
import {Site} from "@shared/models/site";
import {Product} from "@shared/models/product";

import {InboundLoadService} from '../../services/inbound-load.service';
import {ShiftContextService} from '../../services/shift-context.service';

import {InboundLoadStatus} from "../../models/inbound-load-status";
import {InboundLoad} from "../../models/inbound-load";

import {ShiftContext} from "../../models/shift-context";
import {LineItem} from "../../models/shift-validation/loads-summary";
import {ProductService} from "../../services/product.service";
import { ArriveInboundComponent } from './dialogs/arrive-inbound/arrive-inbound.component';
import { UnloadInboundComponent } from './dialogs/unload-inbound/unload-inbound.component';
import { ProcessInboundComponent } from './dialogs/process-inbound/process-inbound.component';

ColumnFilterFormElement.prototype.onModelChange = function (value) {
    this.filterConstraint.value = value;
    if (this.type || value === '') {
        this.dt._filter();
    }
}

@Component({
    selector: 'inbound-loads',
    templateUrl: './inbound-loads.component.html',
    styleUrls: ['./inbound-loads.component.scss']
})
export class InboundLoadsComponent implements OnInit, OnDestroy {
    static readonly RefreshLoadsMessage = {
        severity: 'success',
        summary: 'Refreshing Loads...'
    };

    readonly _plannedLoadDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Planned Inbound Load Deleted',
        detail: ''
    };

    readonly _trailerLeftYardMsg: Message = {
        severity: 'success',
        summary: 'Trailer marked as gone',
        detail: ''
    };

    readonly allCols = [
        {
            field: 'vendorBolNumber', header: 'BOL Number', ariaLabel: 'Sort by BOL Number',
            ariaLabelAsc: 'Sort by BOL Number Ascending', ariaLabelDesc: 'Sort by BOL Number Descending',
            type: 'text'
        },
        {
            field: 'customerSite.chepGlobalLocationId', header: 'Customer/GLID', ariaLabel: 'Sort by GLID',
            ariaLabelAsc: 'Sort by GLID Ascending', ariaLabelDesc: 'Sort by GLID Descending',
            type: 'text'
        },
        {
            field: 'customerSite.partySiteName',
            header: 'Ship From',
            ariaLabel: 'Sort by Ship From',
            ariaLabelAsc: 'Sort by Ship From Ascending',
            ariaLabelDesc: 'Sort by Ship From Descending',
            type: 'text'
        },
        {
            field: 'arrivedTimestamp', header: 'Date Checked In', ariaLabel: 'Sort by Date Checked In',
            ariaLabelAsc: 'Sort by Date Checked In Ascending', ariaLabelDesc: 'Sort by Date Checked In Descending',
            type: 'date'
        },
        {
            field: 'unloadedWorkedShift.date', header: 'Shift Unloaded', ariaLabel: 'Sort by Date Unloaded',
            ariaLabelAsc: 'Sort by Date Unloaded Ascending', ariaLabelDesc: 'Sort by Date Unloaded Descending',
            type: 'date', styleClass: 'unloadCol'
        },
        {
            field: 'processedWorkedShift.date', header: 'Shift Processed', ariaLabel: 'Sort by Date Processed',
            ariaLabelAsc: 'Sort by Date Processed Ascending', ariaLabelDesc: 'Sort by Date Processed Descending',
            type: 'date', styleClass: 'processCol'
        },
        {
            field: 'trailer.trailerNumber', header: 'Trailer', ariaLabel: 'Sort by Trailer Number',
            ariaLabelAsc: 'Sort by Trailer Number Ascending', ariaLabelDesc: 'Sort by Trailer Number Descending',
            type: 'text'
        },
        {
            field: 'carrier.carrierName', header: 'Carrier', ariaLabel: 'Sort by Carrier Name',
            ariaLabelAsc: 'Sort by Carrier Name Ascending', ariaLabelDesc: 'Sort by Carrier Name Descending',
            type: 'text'
        },
        {
            field: 'pooler.accountName',
            header: 'Pooler',
            ariaLabel: 'Sort by Pooler',
            ariaLabelAsc: 'Sort by Pooler',
            ariaLabelDesc: 'Sort by Pooler',
            type: 'text'
        },
        {
            field: 'totalQuantity', header: 'Total Qty', ariaLabel: 'Sort by Total Quantity',
            ariaLabelAsc: 'Sort by Total Quantity Ascending', ariaLabelDesc: 'Sort by Total Quantity Descending',
            type: 'text'
        }
    ];

    ShiftStatus = ShiftStatus;
    InboundLoadStatus = InboundLoadStatus;

    loads: BehaviorSubject<InboundLoad[]> = new BehaviorSubject<InboundLoad[]>([]);
    searchResults: BehaviorSubject<InboundLoad[]> = new BehaviorSubject<InboundLoad[]>([]);
    history: HistoryResponse[];
    context: ShiftContext;

    lineItems: LineItem[] = [];
    products: Product[] = [];

    combinedLoads = combineLatest([this.searchResults, this.loads]).pipe(map(([searchResults, loads]) => ([
        ...loads,
        ...(searchResults?.filter(f => !loads.find(x => x.id === f.id)) ?? [])
    ])));

    subscriptions: Subscription[];
    site: Site;
    date: DateTime;
    shiftId: number;
    subinventories: SiteSubinventory[];
    filteredSubinventories: SiteSubinventory[] = [];
    searchFlag = true;
    showHistory: boolean = false;
    showMinimalHistory: boolean = true;
    historyLoad: InboundLoad = null;
    loading = false;
    hasMoreRows: boolean = false;
    selectedLabel: FileType;
    uploadLoad: InboundLoad;
    uploading: boolean = false;
    showFileStorageDialog: boolean = false;
    canDelete: boolean = false;

    @ViewChild('searchBOL', {static: false}) searchBOL: ElementRef;
    @ViewChild('searchGlid', {static: false}) searchGlid: ElementRef;
    @ViewChild('searchTrailer', {static: false}) searchTrailer: ElementRef;
    @ViewChild('searchUnloadedDate', {static: false}) searchUnloadedDate: Calendar;
    @ViewChild('searchProcessedDate', {static: false}) searchProcessedDate: Calendar;
    @ViewChild('searchCarrier', {static: false}) searchCarrier: ElementRef;
    @ViewChild('searchPooler', {static: false}) searchPooler: ElementRef;
    @ViewChild('op', { static: false }) overlayPanel: OverlayPanel;
    @ViewChild('dt') dt: Table;
    @ViewChild(BlobStorageComponent) blobStorageComponent: BlobStorageComponent;

    showFullWorkflow = new BehaviorSubject<boolean>(false);

    cols = this.showFullWorkflow.pipe(
        map(show => show ? this.allCols : this.allCols.filter(c => c.styleClass !== 'processCol')),
        shareReplay(1));

    dragTarget: InboundLoad;
    dropTarget: InboundLoad;

    constructor(
        private router: Router,
        private inboundLoadService: InboundLoadService,
        private shiftContextService: ShiftContextService,
        private activityService: ActivityBlockingService,
        private messageService: MessageService,
        private authService: AuthService,
        private productService: ProductService,
        private filterService: FilterService,
        private dialogService: DialogService) {
    }

    ngOnInit(): void {

        this.filterService.register('dateFilter', (value: DateTime | null, filter: Date | null): boolean => {
            if (!value)
                return false;
            if (!filter)
                return true;

            const formattedDate = DateTime.fromJSDate(filter);
            if (formattedDate.isValid) {
                return value.equals(formattedDate);
            }
        });

        let initHandle = this.activityService.StartWorking();

        this.subscriptions = [
            this.authService.isUserInAnyRoles([ 'it', 'developers', 'scs admin' ])
                .subscribe(canDelete => this.canDelete = canDelete),

            combineLatest([
                this.shiftContextService.CurrentContext,
                this.shiftContextService.PermittedSites
            ]).subscribe(([context, sites]) => {
                const site = sites.find(s => s.id === context.site.id);
                const info = getSiteInfo(site);

                this.showFullWorkflow.next(site ? info.hasPeco || info.hasIgps : false);
            }),

            this.shiftContextService.CurrentContext.subscribe(context => {
                this.context = context;
                this.subinventories = context?.site.subinventories;
                this.site = context.site;
                this.date = context.date;
                this.shiftId = context.shift.id;
                this.setImportLabels(context.site);
                this.filterSubinventories();
                const loadsHandle = this.activityService.StartWorking();

                if (initHandle) {
                    this.activityService.FinishWorking(initHandle);
                    initHandle = null;
                }

                this.getLoads(loadsHandle);
            }),

            this.productService.CurrentProducts.subscribe(p => {
                this.products = p;
            })
        ];
    }

    getLoads(handle: symbol): void {
        if (this.site.id && this.date && this.shiftId)
            this.inboundLoadService.getInboundLoads(this.site.id, this.date, this.shiftId)
                .subscribe({
                    next:(inboundLoads) => {
                        if (inboundLoads)
                            this.loads.next(inboundLoads);
                        this.activityService.FinishWorking(handle);
                }, error: () => this.activityService.FinishWorking(handle)
                });
    }

    refreshLoads(): void {
        const loadsHandle = this.activityService.StartWorking();

        this.messageService.add(InboundLoadsComponent.RefreshLoadsMessage);

        this.getLoads(loadsHandle);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    getProduct(productId: number): string | undefined {
        return this.products.find(p => p.id === productId)?.itemDescription;
    }

    usesFullWorkflow(load: InboundLoad): boolean {
        const subinv = this.subinventories.find(s => s.id === load.siteSubinventoryId);

        return [
            SubinventoryType.PecoPallets,
            SubinventoryType.PecoComponents,
            SubinventoryType.IgpsPallets
        ].includes(subinv?.subinventoryType);
    }

    filterTable(dt: Table, event: Event, field: string, filter: string = 'contains'): void {
        dt.filter((<HTMLInputElement>event.target).value, field, filter);
        dt.paginator = true;
    }

    startLoad(): void {
        this.router.navigateByUrl('/loads/inbound/new');
    }

    editLoad(load: InboundLoad): void {
        this.router.navigateByUrl('/loads/inbound/' + load.id);
    }

    deleteLoad(load: InboundLoad): void {
        this.inboundLoadService.deleteInboundLoad(load.id).subscribe(_ => {
            this.messageService.add(this._plannedLoadDeletedMsg);
            this.loads.next(this.loads.value.filter(l => l.id !== load.id));
        });
    }

    arriveLoad(load: InboundLoad): void {
        const ref = this.dialogService.open(ArriveInboundComponent, {
            data: { load: load },
            header: 'Check In Load ' + (load?.vendorBolNumber ?? load?.propakPoNumber ?? '')
        });
        ref.onClose.subscribe(load => this.onLoadStatusChanged(load));
    }

    unloadLoad(load: InboundLoad): void {
        if (!this.usesFullWorkflow(load)) {
            this.processLoad(load);
        }
        else {
            const ref = this.dialogService.open(UnloadInboundComponent, {
                data: { load: load },
                header: 'Unload Load ' + (load?.vendorBolNumber ?? load?.propakPoNumber ?? '')
            });
            ref.onClose.subscribe(load => this.onLoadStatusChanged(load));
        }
    }

    processLoad(load: InboundLoad): void {
        const ref = this.dialogService.open(ProcessInboundComponent, {
            data: { load: load },
            header: (this.showFullWorkflow.value ? 'Process Load ' : 'Unload Load ')
                + (load?.vendorBolNumber ?? load?.propakPoNumber ?? '')
        });
        ref.onClose.subscribe(load => this.onLoadStatusChanged(load));
    }

    onLoadStatusChanged(load: InboundLoad | undefined): void {
        if (!load)
            return;

        if (this.loads.value.some(l => l.id === load.id)) {
            this.loads.next(this.loads.value.map(l => l.id === load.id ? load : l));
        }

        if (this.searchResults.value.some(l => l.id === load.id)) {
            this.searchResults.next(this.searchResults.value.map(l => l.id === load.id ? load : l));
        }
    }

    changeSearchStatus(): void {
        if (this.searchFlag === false) {
            this.searchResults.next([]);
            this.hasMoreRows = false;
        }
        this.dt.reset();
    }

    searchLoad(): void {
        this.loading = true;
        const filters = <any>this.dt.filters.valueOf();

        this.inboundLoadService.searchInboundLoads({
            siteId: this.site.id,
            bolNumber: filters['vendorBolNumber'].value ?? '',
            glid: filters['customerSite.chepGlobalLocationId'].value ?? '',
            shipFrom: filters['customerSite.partySiteName'].value ?? '',
            trailerNumber: filters['trailer.trailerNumber'].value ?? '',
            carrierScac: filters['carrier.carrierName'].value ?? '',
            pooler: filters['pooler.accountName'].value ?? '',
            unloadedDate: filters['unloadedWorkedShift.date'].value ? DateTime.fromJSDate(this.searchUnloadedDate?.value).toISO() : '',
            processedDate: filters['processedWorkedShift.date']?.value ? DateTime.fromJSDate(this.searchProcessedDate?.value).toISO() : ''
        })
            .subscribe(searched => {
                if (!!searched) {
                    this.searchResults.next(searched.resultSet);
                    this.hasMoreRows = searched.hasMoreRows;
                    this.loading = false;
                } else {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'No Loads Found',
                        detail: 'Refine Search Criteria and try again.'
                    });
                    this.loading = false;
                }
            });
    }

    fromSearch(id: number): boolean {
        if (!!this.searchResults)
            return !!this.searchResults.value.find(sr => sr.id === id);
        else
            return false;
    }

    showHistoryDialog(load: InboundLoad): void {
        this.historyLoad = load;
        this.inboundLoadService.getHistory(this.site.id, load.id).subscribe({
            next: (h) => {
                if (h) {
                    this.history = h;
                    this.showHistory = true;
                }
        }, error: () => this.showHistory = false});

    }

    get filteredHistory(): HistoryResponse[] {
        if (!this.history) {
            return [];
        }
        if (!this.showMinimalHistory) {
            return this.history;
        }
        return this.history.filter(record =>
            record.dataType !== "Trailer"
            || (
                record.action === 'add'
                || (
                    record.action === 'update'
                    && Object.hasOwn(record.history, "TrailerNumber")
                )
            )
        );
    }

    markTrailerLeft(load: InboundLoad): void {
        this.inboundLoadService.markTrailerLeft(load.id, this.context.id).subscribe(i => {
            this.messageService.add(this._trailerLeftYardMsg);
            this.loads.next(this.loads.value.filter(l => l.id !== i.id));
        });
    }

    parseDate(date: Date): DateTime {
        return DateTime.fromJSDate(date);
    }

    updateSubinventoryFilter(e: string): void {
        this.selectedLabel = <FileType>e;
        this.filterSubinventories()
    }

    filterSubinventories(): void {
        if (this.subinventories && this.selectedLabel) {
            switch (<FileType>this.selectedLabel) {
                case FileType.cheploads:
                    this.filteredSubinventories = this.subinventories.filter(s => s.isActive
                        && (s.subinventoryType === SubinventoryType.ChepPlantPallets
                            || s.subinventoryType === SubinventoryType.ChepPalletStorage));
                    break;
                case FileType.walmartloads:
                    this.filteredSubinventories = this.subinventories.filter(s => s.isActive
                        && s.subinventoryType === SubinventoryType.TpmProduction);
                    break;
                default:
                    this.filteredSubinventories;
            }
            this.filteredSubinventories.sort((l, r) => l.subinventoryType === r.subinventoryType
                ? l.name.localeCompare(r.name)
                : l.subinventoryType - r.subinventoryType)
        }
    }

    setImportLabels(site: Site): string | string[] {
        if (!site)
            return null;

        const siteInfo = getSiteInfo(site);

        let labels: string[] = [];
        const showChep = siteInfo.hasPlant || siteInfo.hasStorage
        const showWalmart = siteInfo.isSortOnlyRlc || siteInfo.isRlc;

        if (showChep)
            labels.push(FileType.cheploads)
        if (showWalmart)
            labels.push(FileType.walmartloads);

        this.selectedLabel = <FileType>labels[0];
        return labels.length == 1 ? labels[0] : labels;
    }

    uploadDocument(load: InboundLoad): void {
        this.showFileStorageDialog = true;
        this.uploadLoad = load;
    }

    overlayToggle(event: any, lineItems: LineItem[]) {
        this.lineItems = lineItems;
        this.overlayPanel.show(event);
    }

    onShow(): void {
        if (this.uploadLoad == null)
            this.blobStorageComponent.getFiles();
    }

    onHide(): void {
        this.showFileStorageDialog = false;
        this.uploadLoad = null;
        this.blobStorageComponent.documentIds = [];
    }

    dropHandler(event: DragEvent): void {
        event.preventDefault();
        this.uploading = true;
        this.dropTarget = this.dragTarget;
        const fileResultObs = (Array.from(event.dataTransfer.items)?.filter(i => i.kind === 'file')
            .map(i => i.getAsFile()) ?? Array.from(event.dataTransfer.files))
            .map((file, i) => this.blobStorageComponent.dragOverFunction(file, this.site.id, this.dropTarget.id, 'InboundLoads'));

        combineLatest(fileResultObs).subscribe({
            next: (fileIds) => {
                this.dropTarget.documentIds.push(...fileIds);
                this.uploading = false;
            },
            error: () => {
                this.uploading = false; this.dropTarget = null;
            }
        });
        this.dragLeaveHandler();
    }

    dragOverHandler(event: Event, load: InboundLoad): void {
        event.preventDefault();
        this.dragTarget = load;
    }
    dragLeaveHandler(): void {
        this.dragTarget = null;
    }

    dragged(id: number): boolean {
        return !!this.dragTarget && this.dragTarget.id === id;
    }

    dropped(id: number): boolean {
        return !!this.dropTarget && this.dropTarget.id === id;
    }

}
